import * as Sentry from "@sentry/react";
import { debounce } from "lodash";
import { useCallback, useEffect, useMemo, useRef } from "react";

type UseAutoSaveOptions<T> = {
  saveFn: (entity: T) => Promise<void> | void;
  entity: T;
  createSnapshot?: () => Promise<void>;
  readOnly?: boolean;
  debounceMs?: number;
  version?: string | number | null;
  // Explicit callers accept persistence transitions through reconcile, not raw props.
  versionPolicy?: "automatic" | "explicit";
};

/**
 * Shared hook for auto-saving Story Bible entities with multi-tab protection
 *
 * Handles:
 * - Debounced saves
 * - Flushing saves on field blur (including visibility change blur from TiptapField)
 * - Optional snapshot creation after save
 * - Waits for in-flight saves before creating snapshots
 *
 * NOTE: Tab visibility change handling is done at the field level (TiptapField)
 * to prevent unnecessary saves.
 *
 * @example
 * const { handleBlur, debouncedSave } = useAutoSave({
 *   saveFn: (char) => saveCharacter(char),
 *   entity: character,
 *   createSnapshot: () => createCharacterSnapshot(character.id),
 * });
 */
export function useAutoSave<T>({
  saveFn,
  entity,
  createSnapshot,
  readOnly = false,
  debounceMs = 1000,
  version,
  versionPolicy = "automatic",
}: UseAutoSaveOptions<T>) {
  const versionRef = useRef(version);
  const generation = useRef(0);
  const mounted = useRef(true);
  const current = useRef({ saveFn, readOnly });
  current.current = { saveFn, readOnly };
  const inFlightSaveRef = useRef<Promise<void>>(Promise.resolve());

  const enqueue = useCallback((entityToSave: T) => {
    const queuedGeneration = generation.current;
    const next = inFlightSaveRef.current.catch(() => {}).then(async () => {
      if (current.current.readOnly || queuedGeneration !== generation.current) return;
      await current.current.saveFn(entityToSave);
    });
    inFlightSaveRef.current = next;
    // Observe failures even when no blur is waiting. Later saves can still proceed.
    void next.catch(() => {});
    return next;
  }, []);

  const debouncedSave = useMemo(() => {
    const save = debounce(enqueue, debounceMs);
    const cancelTimer = save.cancel;
    save.cancel = () => {
      generation.current += 1;
      cancelTimer();
    };
    return save;
  }, [enqueue, debounceMs]);

  // Call before a save callback resolves. A React effect is too late to stop the
  // next queued promise from sending an obsolete snapshot.
  const reconcile = useCallback((acceptedVersion: typeof version, replacement?: T) => {
    versionRef.current = acceptedVersion;
    debouncedSave.cancel();
    if (replacement !== undefined) enqueue(replacement);
  }, [debouncedSave, enqueue]);

  useEffect(() => {
    if (versionPolicy === "automatic" && versionRef.current !== version) reconcile(version);
  }, [version, versionPolicy, reconcile]);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      // Closing a card or Outline must not discard its edits. The promise queue
      // retains valid work and response-driven replacements after unmount.
      // Only explicit cancellation or version reconciliation invalidates saves.
      debouncedSave.flush();
    };
  }, [debouncedSave]);

  const handleBlur = useCallback(async () => {
    if (readOnly || !entity) return;
    let stopped = false;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    const drain = async () => {
      while (!stopped && mounted.current) {
        debouncedSave.flush();
        const tail = inFlightSaveRef.current;
        await tail.catch(() => {});
        if (stopped || !mounted.current) return;
        debouncedSave.flush();
        if (tail === inFlightSaveRef.current) return;
      }
    };
    const timeout = new Promise<void>((resolve) => {
      timeoutId = setTimeout(() => {
        if (mounted.current) {
          Sentry.captureMessage("AutoSave timeout exceeded", {
            level: "warning",
            extra: { entityType: entity?.constructor?.name, timeout: 5000 },
          });
        }
        resolve();
      }, 5000);
    });
    try {
      await Promise.race([drain(), timeout]);
      stopped = true;
      clearTimeout(timeoutId);
      if (mounted.current && !current.current.readOnly) await createSnapshot?.();
    } catch (err) {
      Sentry.captureException(err);
    } finally {
      stopped = true;
      clearTimeout(timeoutId);
    }
  }, [readOnly, entity, debouncedSave, createSnapshot]);

  return { handleBlur, debouncedSave, reconcile };
}
